"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { getEffectiveStatus } from "@/app/[tenant]/(console)/scribe/playground/[id]/weight-loss/dose-table";
import { DoseDilutionLabel } from "@/components/dose/dilution-label";
import CollapsibleText from "@/components/ui/collapsible-text";
import { useNoteDoseScope } from "@/hooks/use-note-dose-scope";
import { formatDoseComponentAmount, getDoseComponentName, getDoseComponents } from "@/lib/dose-components";
import { formatDate, toClinicDateKey } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { DoseManagementStatus, type MedicationSummary } from "@/types/medication";
import type { PatientChart } from "@/types/patient";

/** Visit days shown before "Show all". */
const MAX_VISIBLE_DAYS = 5;

/** Sites listed under a dose before the rest fold behind "+N more". */
const MAX_SITES = 4;
function doseDate(dose: MedicationSummary): string {
  return dose.doseDate ?? dose.createdDate;
}
interface DoseSite {
  label: string;
  amount?: number | null;
}

/** Where the dose went, with the amount given at each site when recorded. */
function doseSites(dose: MedicationSummary): DoseSite[] {
  const rows = dose.locations ?? [];
  if (rows.length > 0) return rows;
  const text = dose.administrationLocation?.trim();
  return text ? [{
    label: text
  }] : [];
}
interface DoseDay {
  key: string;
  doses: MedicationSummary[];
}

/** Doses grouped by the clinic day they were given, keeping their order. */
function groupByDay(doses: MedicationSummary[]): DoseDay[] {
  const days: DoseDay[] = [];
  for (const dose of doses) {
    const key = toClinicDateKey(doseDate(dose));
    const last = days[days.length - 1];
    if (last?.key === key) last.doses.push(dose);else days.push({
      key,
      doses: [dose]
    });
  }
  return days;
}

/** One medication per line, name and amount; a mixed dose lists each part. */
function DoseRow(t0) {
  const $ = _c(10);
  const {
    dose
  } = t0;
  const [showAllSites, setShowAllSites] = useState(false);
  let t1;
  if ($[0] !== dose) {
    t1 = dose.managementStatus === DoseManagementStatus.DISCONTINUED || getEffectiveStatus(dose) !== "active";
    $[0] = dose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stopped = t1;
  let t2;
  let t3;
  if ($[2] !== dose || $[3] !== showAllSites || $[4] !== stopped) {
    const components = getDoseComponents(dose);
    const sites = doseSites(dose);
    const siteRows = sites.some(_temp) ? sites : [];
    const visibleSites = showAllSites ? siteRows : siteRows.slice(0, MAX_SITES);
    const hidden = siteRows.length - visibleSites.length;
    const summary = [siteRows.length ? null : sites.map(_temp2).join(", "), dose.frequency, dose.dosespotMedicationId ? "eRx" : null].filter(Boolean).join(" \xB7 ");
    t2 = components.map((component, index) => {
      const name = getDoseComponentName(component);
      return <div key={component.id ?? index} className="flex items-baseline gap-2"><div className="flex min-w-0 flex-1 items-center gap-1.5"><span className={cx("truncate text-body font-medium", stopped ? "text-foreground-secondary line-through" : "text-foreground")} title={stopped ? `${name} (discontinued)` : name}>{name}</span>{index === 0 && <DoseDilutionLabel components={components} />}</div><span className={cx("shrink-0 text-body tabular-nums", stopped ? "text-foreground-secondary line-through" : "text-foreground")}>{formatDoseComponentAmount(component)}</span></div>;
    });
    t3 = (summary || siteRows.length > 0) && <ul className="mt-0.5 border-l border-border pl-2">{summary && <li className="truncate text-caption text-foreground-secondary">{summary}</li>}{visibleSites.map(_temp3)}{hidden > 0 && <li className="text-caption"><button type="button" className="text-foreground-secondary hover:text-foreground" onClick={() => setShowAllSites(true)}>+{hidden} more</button></li>}</ul>;
    $[2] = dose;
    $[3] = showAllSites;
    $[4] = stopped;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <li>{t2}{t3}</li>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp3(site_1, index_0) {
  return <li key={`${site_1.label}-${index_0}`} className="flex justify-between gap-2 text-caption"><span className="truncate text-foreground-secondary">{site_1.label}</span>{site_1.amount != null && <span className="shrink-0 tabular-nums text-foreground">{site_1.amount}</span>}</li>;
}
function _temp2(site_0) {
  return site_0.label;
}
function _temp(site) {
  return site.amount != null;
}
function DoseDays(t0) {
  const $ = _c(4);
  const {
    days
  } = t0;
  let t1;
  if ($[0] !== days) {
    t1 = days.map(_temp5);
    $[0] = days;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="space-y-3">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp5(day) {
  return <section key={day.key}><h5 className="mb-1 text-caption text-foreground-secondary">{formatDate(day.key)}</h5><ul className="space-y-2">{day.doses.map(_temp4)}</ul></section>;
}
function _temp4(dose) {
  return <DoseRow key={dose.id} dose={dose} />;
}
interface Props {
  patient: PatientChart;
  /** Doses charted on this note show in the note itself, not here. */
  currentNoteId?: string;
}

/** Doses and prescriptions before the open note's visit, newest day first. */
export default function PastDosesSection(t0) {
  const $ = _c(17);
  const {
    patient,
    currentNoteId
  } = t0;
  const {
    isDoseForNote,
    isAfterNoteVisit
  } = useNoteDoseScope(currentNoteId);
  let t1;
  if ($[0] !== isAfterNoteVisit || $[1] !== isDoseForNote || $[2] !== patient.medications) {
    let t2;
    if ($[4] !== isAfterNoteVisit || $[5] !== isDoseForNote) {
      t2 = dose => !isDoseForNote(dose) && !isAfterNoteVisit(dose);
      $[4] = isAfterNoteVisit;
      $[5] = isDoseForNote;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = groupByDay((patient.medications ?? []).filter(t2).sort(_temp6));
    $[0] = isAfterNoteVisit;
    $[1] = isDoseForNote;
    $[2] = patient.medications;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const days = t1;
  if (days.length === 0) {
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <p className="text-caption text-muted-foreground">None recorded</p>;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t2;
  if ($[8] !== days) {
    t2 = days.slice(0, MAX_VISIBLE_DAYS);
    $[8] = days;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <DoseDays days={t2} />;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== days) {
    t4 = days.length > MAX_VISIBLE_DAYS && <CollapsibleText className="mt-3" text={`Show all ${days.length} visits`} collapsedText="Show less" textClassName="text-caption"><div className="mt-3"><DoseDays days={days.slice(MAX_VISIBLE_DAYS)} /></div></CollapsibleText>;
    $[12] = days;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <div>{t3}{t4}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp6(a, b) {
  return new Date(doseDate(b)).getTime() - new Date(doseDate(a)).getTime();
}